import React, { useState, useEffect, useReducer, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import CollectionData from './CollectionData';
import CollectionImage from './CollectionImage';
import CollectionFilling from './CollectionFilling';
import CollectionFilters from './CollectionFilters';

import PageTitle from '../../../common/PageTitle';
import Button from '../../../common/Button';
import UnsavedHeader from '../../../common/UnsavedHeader';

import {
  fetchGetCollectionById,
  fetchSaveCollection,
  fetchEditCollection,
  fetchRemoveCollection,
  fetchGetCollectionFilters,
  fetchUpdateAllCollectionFilters,
} from '../../../../actions/collections';

import { fetchGetAttributes } from '../../../../actions/items';

import { parseError } from '../../../../helpers';

const validationSchema = Yup.object().shape({
  //TODO Validation
});

export const radiosData = [
  {
    label: 'всем условиям',
    value: 'AND',
  },
  {
    label: 'любому условию',
    value: 'OR',
  },
];

export const filterTypes = [
  { label: 'Цена', value: 'PRICE' },
  { label: 'Атрибут', value: 'ATTRIBUTE' },
  { label: 'Остатки', value: 'RESTS' },
  { label: 'Вес', value: 'WEIGHT' },
  { label: 'Бренд', value: 'BRAND_NAME' },
  { label: 'Наименование', value: 'NAME' },
];

export const compareOperations = [
  { label: 'Содержит', value: 'CONTAINS' }, //0
  { label: 'Не содержит', value: 'NOT_CONTAINS' }, //1
  { label: 'Начинается с', value: 'STARTS_WITH' }, //2
  { label: 'Заканчивается на', value: 'ENDS_WITH' }, //3
  { label: 'Равно', value: 'EQUALS' }, //4
  { label: 'Больше', value: 'GREATER_THAN' }, //5
  { label: 'Меньше', value: 'LESS_THAN' }, //6
];

const getInitialState = (props) => {
  const isNew = props.match.params.id === 'new';
  return {
    initialized: false,
    isNew,
    collectionId: !isNew ? props.match.params.id : null,
    fillType: 'MANUAL',
    currentProducts: [],
    logicMode: 'AND',
    filters: [],
    collectionFilters: [],
    attributes: [],
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setInitialize':
      return { ...state, initialized: true };
    case 'updateCollection':
      return { ...state, currentCollection: action.collection, isNew: false };
    case 'updateFillType':
      return { ...state, fillType: action.fillType };
    case 'updateCurrentProducts':
      return { ...state, currentProducts: action.currentProducts };
    case 'updateFiltersLogicMode':
      return { ...state, logicMode: action.logicMode };
    case 'updateFilters':
      return { ...state, filters: action.filters };
    case 'updateCollectionFilters':
      return { ...state, collectionFilters: action.collectionFilters };
    case 'updateAttributes':
      return { ...state, attributes: action.attributes };
    default:
      return state;
  }
};

const SingleCollection = (props) => {
  const { history, setCustomHeader } = props;

  const [state, dispatch] = useReducer(reducer, getInitialState(props));
  const [loading, setLoading] = useState(false);
  const [unsaved, setUnsavedStatus] = useState(false);
  const reduxDispatch = useDispatch();

  const {
    initialized,
    isNew,
    collectionId,
    currentCollection,
    fillType,
    currentProducts,
    logicMode,
    filters,
    collectionFilters,
  } = state;

  const formik = useFormik({
    initialValues: { title: 'Новая коллекция' },
    onSubmit: async () => {
      if (isNew) {
        const collection = await saveCollection();
        const collectionFilters = await reduxDispatch(fetchGetCollectionFilters(collection.id));
        dispatch({ type: 'updateCollectionFilters', collectionFilters: collectionFilters || [] });
      } else {
        await editCollection();
      }
    },
  });
  const {
    values,
    errors,
    submitCount,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
    isValid,
  } = formik;

  useEffect(() => {
    (async function () {
      if (!isNew) {
        const result = await Promise.all([
          reduxDispatch(fetchGetCollectionById(collectionId)),
          reduxDispatch(fetchGetCollectionFilters(collectionId)),
          reduxDispatch(fetchGetAttributes()),
        ]);

        const collection = result[0];
        const collectionFilters = result[1] || [];
        const attributes = result[2];

        dispatch({ type: 'updateCollection', collection });
        dispatch({ type: 'updateCollectionFilters', collectionFilters });
        dispatch({ type: 'updateAttributes', attributes });
      }
      dispatch({ type: 'setInitialize' });
    })();
  }, []);

  useEffect(() => {
    if (currentCollection) {
      setValues({
        title: currentCollection.title,
        description: currentCollection.description,
      });
      dispatch({ type: 'updateFillType', fillType: currentCollection.fillType });
      if (currentCollection.fillType === 'MANUAL') {
        const currentProducts = !currentCollection.products ? [] : currentCollection.products;
        dispatch({ type: 'updateCurrentProducts', currentProducts });
      } else {
        dispatch({ type: 'updateFiltersLogicMode', logicMode: currentCollection.logicMode });
        const filters = !currentCollection.filters
          ? []
          : currentCollection.filters.map((f) => {
              return {
                ...f,
                id: uuidv4(),
                filterType: filterTypes.find((t) => t.value === f.filterType),
                compareOperation: compareOperations.find((o) => o.value === f.compareOperation),
              };
            });
        dispatch({ type: 'updateFilters', filters });
      }
    }
  }, [currentCollection]);

  useEffect(() => {
    setCustomHeader(
      unsaved ? (
        <UnsavedHeader handleSubmit={handleSubmit} loading={loading} disabled={!isValid} />
      ) : null,
    );
    return () => {
      setCustomHeader(null);
    };
  }, [unsaved, loading, isValid]);

  const saveCollection = () => {
    setLoading(true);

    const saveObj = {
      title: values.title,
      description: values.description,
    };

    if (fillType === 'MANUAL') {
      saveObj.fillType = 'MANUAL';
      saveObj.products = currentProducts;
      saveObj.filters = [];
    }

    if (fillType === 'AUTO') {
      saveObj.fillType = 'AUTO';
      saveObj.products = [];
      saveObj.logicMode = logicMode;
      saveObj.filters = filters.map((f, i) => ({
        num: i + 1,
        filterType: f.filterType.value,
        compareOperation: f.compareOperation.value,
        compareValue: f.compareValue,
      }));
    }

    return reduxDispatch(fetchSaveCollection(saveObj))
      .then((collection) => {
        toast.info('Коллекция успешно сохранена');
        dispatch({ type: 'updateCollection', collection });
        history.push('/collections/' + collection.id);
        return collection;
      })
      .catch((err) => {
        toast.error(parseError(err));
      })
      .finally(() => {
        setUnsavedStatus(false);
        setLoading(false);
      });
  };

  const editCollection = () => {
    setLoading(true);

    let saveObj = {
      ...currentCollection,
      title: values.title,
      description: values.description,
    };

    if (fillType === 'MANUAL') {
      saveObj.fillType = 'MANUAL';
      saveObj.products = currentProducts;
      saveObj.filters = [];
    }

    if (fillType === 'AUTO') {
      saveObj.fillType = 'AUTO';
      saveObj.products = [];
      saveObj.logicMode = logicMode;
      saveObj.filters = filters.map((f, i) => ({
        num: i + 1,
        filterType: f.filterType.value,
        compareOperation: f.compareOperation.value,
        compareValue: f.compareValue,
      }));
    }

    return reduxDispatch(fetchEditCollection(saveObj))
      .then((collection) => {
        toast.info('Коллекция успешно сохранена');
        return collection;
      })
      .catch((err) => {
        toast.error(parseError(err));
      })
      .finally(() => {
        setUnsavedStatus(false);
        setLoading(false);
      });
  };

  const editAllCollectionFilters = async (allFilters) => {
    const updatedFilters = handleUpdateCollectionFilters(allFilters);

    const filtersToUpdate = {
      filterBlocks: [],
      success: true,
    };

    filtersToUpdate.filterBlocks = updatedFilters;

    await reduxDispatch(fetchUpdateAllCollectionFilters(filtersToUpdate));
  };

  const deleteCollection = () => {
    reduxDispatch(fetchRemoveCollection(currentCollection.id))
      .then(() => {
        toast.info('Коллекция успешно удалена');
        history.push('/collections');
      })
      .catch((err) => {
        toast.error(err.data.errors[0].message);
      });
  };

  const handleSetFillType = (value) => {
    dispatch({ type: 'updateFillType', fillType: value });
    setUnsavedStatus(true);
  };

  const handleUpdateProducts = (products) => {
    products.forEach((item, index) => {
      item.collectionPos = index;
    });
    dispatch({ type: 'updateCurrentProducts', currentProducts: products });
    setUnsavedStatus(true);
  };

  const handleUpdateFiltersLogicMode = (value) => {
    dispatch({ type: 'updateFiltersLogicMode', logicMode: value });
    setUnsavedStatus(true);
  };

  const handleUpdateFilters = (filters) => {
    dispatch({ type: 'updateFilters', filters });
    setUnsavedStatus(true);
  };

  const handleUpdateCollectionFilters = (data) => {
    const newFiltersBlocks = collectionFilters.slice();
    let currentArr = [];
    let currentBlockIndex = 0;
    let currentFilterIndex = 0;

    data.forEach((f, i) => {
      if (f.type === 'separator') {
        newFiltersBlocks[currentBlockIndex] = {
          ...newFiltersBlocks[currentBlockIndex],
          filters: currentArr,
        };
      }

      if (f.type === 'separator') {
        currentBlockIndex++;
        currentFilterIndex = 0;
        currentArr = [];
      } else {
        currentArr.push({ ...f, ordering: currentFilterIndex });
        currentFilterIndex++;
      }

      if (i === data.length - 1) {
        newFiltersBlocks[currentBlockIndex] = {
          ...newFiltersBlocks[currentBlockIndex],
          filters: currentArr,
        };
      }
    });

    dispatch({ type: 'updateCollectionFilters', collectionFilters: newFiltersBlocks });

    return newFiltersBlocks;
  };

  if (!initialized) return null;

  const pageTitle = !isNew && currentCollection ? currentCollection.title : 'Новая коллекция';
  const breadcrumbs = [{ title: 'Коллекции', route: '/collections' }, { title: pageTitle }];

  return (
    <Fragment>
      <PageTitle title={pageTitle} breadcrumbs={breadcrumbs} />
      <div className="row">
        <div className="col-lg-8">
          <CollectionData
            values={values}
            errors={errors}
            isNew={isNew}
            loading={loading}
            handleSubmit={handleSubmit}
            currentCollection={currentCollection}
            deleteCollection={deleteCollection}
            setFieldValue={setFieldValue}
            setUnsavedStatus={setUnsavedStatus}
          />
          {isNew || !currentCollection.isAllProducts ? (
            <CollectionFilling
              isNew={isNew}
              collection={currentCollection}
              fillType={fillType}
              currentProducts={currentProducts}
              logicMode={logicMode}
              filters={filters}
              handleSetFillType={handleSetFillType}
              handleUpdateProducts={handleUpdateProducts}
              handleUpdateFiltersLogicMode={handleUpdateFiltersLogicMode}
              handleUpdateFilters={handleUpdateFilters}
            />
          ) : null}
        </div>
        <div className="col-lg-4">
          <CollectionImage currentCollection={currentCollection} saveCollection={saveCollection} />
          <CollectionFilters
            collectionFilters={collectionFilters}
            editAllCollectionFilters={editAllCollectionFilters}
          />
        </div>
      </div>
      {isNew || !currentCollection.isAllProducts ? (
        <div className="row mb-3">
          <div className="col-12">
            <div className="row" style={{ margin: 0 }}>
              <Button
                type="button"
                text="Сохранить"
                disabled={loading}
                loading={loading}
                onClick={handleSubmit}
              />
              {!isNew ? (
                <button
                  type="button"
                  className="btn btn-danger waves-effect waves-light"
                  onClick={deleteCollection}
                  style={{ marginLeft: 'auto' }}
                >
                  Удалить
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </Fragment>
  );
};

export default SingleCollection;
